/**
 * Pre-Trade Coach Panel — historical context BEFORE the trade is taken.
 *
 * Mounts at the top of AddEditTradeModal. Reacts to changes in the trade
 * draft's setup / atmStrategy / direction / entryTime fields and surfaces:
 *
 *   - Historical match: how this setup (× ATM × direction × time) has
 *     played out for THIS trader, not generic stats. WR + count + avg R.
 *   - Top winning confluences: which confluences were on the winners of
 *     similar trades — the "winning recipe" the trader can check against
 *     what they're seeing right now.
 *   - Time-of-day WR: how this setup performs in the entry-hour bucket vs
 *     the setup's overall baseline. Surfaces "you're trading this setup at
 *     a weak hour" warnings before the trade is taken.
 *   - Recommended confluences: the setupDetails.recommendedConfluences
 *     pinned by the trader, with a check on whether they're currently
 *     flagged on the trade draft.
 *
 * Why this matters separately from the post-trade Coach:
 *   Post-trade Coach explains what happened. Pre-trade Coach changes what
 *   happens. The infrastructure (analyzers, profile, scope orchestrators)
 *   already knows the trader's edges and leaks — surfacing them at the
 *   decision point is a direct behavior intervention, not a retrospective.
 *
 * Pure (mostly): all stats are derived from props via useMemo. No state of
 * its own except UI affordances. The panel auto-collapses when not enough
 * setup info is present (no setup selected → no panel).
 *
 * Props:
 *   trade              The current trade draft from AddEditTradeModal
 *   allTrades          Full closed-trade history (for historical match)
 *   allConfluences     Confluence library (for label resolution)
 *   setupDetails       Setup metadata (for recommended confluences check)
 *   getTradeNetPnL     (trade, account) => { netPnl } — for commission-aware WR
 *   getAccountById     (accountId) => account|null
 *   onJumpToTrade      Optional: (tradeId, date) => void — for "see similar" jump
 *   darkMode           bool
 */

import { useMemo } from 'react';
import { Brain, Target, Clock, AlertTriangle, CheckCircle2, TrendingUp, ExternalLink } from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────

const fmtPct = (n) => Number.isFinite(n) ? `${Math.round(n * 100)}%` : '—';
const fmtR = (n) => Number.isFinite(n) ? `${n >= 0 ? '+' : ''}${n.toFixed(2)}R` : '—';

const hourOf = (timeStr) => {
  if (!timeStr || typeof timeStr !== 'string') return null;
  const m = timeStr.match(/^(\d{1,2}):(\d{2})/);
  if (!m) return null;
  const h = parseInt(m[1], 10);
  return Number.isFinite(h) && h >= 0 && h <= 23 ? h : null;
};

const fmtHour = (h) => {
  if (h == null) return '';
  const ampm = h >= 12 ? 'PM' : 'AM';
  const display = h === 0 ? 12 : (h > 12 ? h - 12 : h);
  return `${display}:00 ${ampm}`;
};

const isWin = (t) => Number.isFinite(t.netPnl) && t.netPnl > 0;

// ── Component ────────────────────────────────────────────────────────────

const PreTradeCoachPanel = ({
  trade,
  allTrades = [],
  allConfluences = [],
  setupDetails = {},
  getTradeNetPnL,
  getAccountById,
  onJumpToTrade,
  darkMode = true,
}) => {
  const setup = trade?.setup;
  const atmStrategy = trade?.atmStrategy;
  const direction = trade?.direction;
  const entryTime = trade?.entryTime;

  // Bail entirely until we have at least a setup. No need to clutter the
  // modal before the user has picked something.
  const insights = useMemo(() => {
    if (!setup || !Array.isArray(allTrades) || allTrades.length === 0) return null;

    const labelMap = new Map();
    for (const c of allConfluences) {
      if (c?.id) labelMap.set(c.id, c.label || c.name || c.id);
    }

    // Enrich with netPnl up-front (same pattern as scopes).
    const netPnlFor = (t) => {
      const acc = getAccountById ? getAccountById(t.accountId) : null;
      const r = getTradeNetPnL ? getTradeNetPnL(t, acc) : { netPnl: parseFloat(t.pnl) || 0 };
      return Number.isFinite(r?.netPnl) ? r.netPnl : 0;
    };
    const enriched = allTrades.map(t => ({ ...t, netPnl: netPnlFor(t) }));

    // Tier-by-tier match: most-specific (setup × ATM × direction) first,
    // fall back to less-specific buckets if the strict bucket is too small.
    const matchSetupOnly       = enriched.filter(t => t.setup === setup);
    const matchSetupAtm        = atmStrategy ? matchSetupOnly.filter(t => t.atmStrategy === atmStrategy) : matchSetupOnly;
    const matchSetupAtmDir     = direction   ? matchSetupAtm.filter(t => t.direction === direction)     : matchSetupAtm;

    // Pick the most-specific bucket with ≥ 4 trades. Slip down tiers if needed.
    let baseTrades = matchSetupAtmDir;
    let matchLabel = `${setup}${atmStrategy ? ` × ${atmStrategy}` : ''}${direction ? ` × ${direction}` : ''}`;
    if (baseTrades.length < 4 && matchSetupAtm.length >= 4) {
      baseTrades = matchSetupAtm;
      matchLabel = `${setup}${atmStrategy ? ` × ${atmStrategy}` : ''}`;
    }
    if (baseTrades.length < 4) {
      baseTrades = matchSetupOnly;
      matchLabel = setup;
    }
    if (baseTrades.length < 1) return null;

    const wins = baseTrades.filter(isWin);
    const wr = baseTrades.length > 0 ? wins.length / baseTrades.length : 0;
    const rs = baseTrades.map(t => parseFloat(t.rMultiple)).filter(Number.isFinite);
    const avgR = rs.length > 0 ? rs.reduce((s, r) => s + r, 0) / rs.length : null;

    // Top confluences in winners — the "winning recipe" check
    const winnerConfFreq = new Map();
    for (const t of wins) {
      const conf = Array.isArray(t.confluences) ? t.confluences : [];
      for (const cid of conf) winnerConfFreq.set(cid, (winnerConfFreq.get(cid) || 0) + 1);
    }
    const topWinningConf = [...winnerConfFreq.entries()]
      .filter(([, n]) => wins.length > 0 && (n / wins.length) >= 0.4)  // appears in ≥40% of winners
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([id, n]) => ({
        id, label: labelMap.get(id) || id,
        winnerShare: n / wins.length,
        currentlyTagged: Array.isArray(trade.confluences) && trade.confluences.includes(id),
      }));

    // Time-of-day fit — only when entryTime is set
    let hourFit = null;
    const h = hourOf(entryTime);
    if (h != null && matchSetupOnly.length >= 6) {
      const hourTrades = matchSetupOnly.filter(t => hourOf(t.entryTime) === h);
      if (hourTrades.length >= 3) {
        const hourWr = hourTrades.filter(isWin).length / hourTrades.length;
        const baselineWr = matchSetupOnly.filter(isWin).length / matchSetupOnly.length;
        hourFit = {
          hour: h,
          window: `${fmtHour(h)}-${fmtHour((h + 1) % 24)}`,
          hourWr,
          hourCount: hourTrades.length,
          baselineWr,
          delta: hourWr - baselineWr,
        };
      }
    }

    // Recommended confluences — what the trader pinned vs what's currently flagged
    const recommended = setupDetails?.[setup]?.recommendedConfluences || [];
    const currentConf = new Set(Array.isArray(trade.confluences) ? trade.confluences : []);
    const recommendedStatus = recommended.map(id => ({
      id,
      label: labelMap.get(id) || id,
      present: currentConf.has(id),
    }));
    const missingRecommended = recommendedStatus.filter(r => !r.present);

    // Most-similar prior trade for "see similar" jump
    const sortByRecency = [...baseTrades].sort((a, b) =>
      (b.date || '').localeCompare(a.date || '')
    );
    const mostRecent = sortByRecency[0] || null;

    return {
      matchLabel,
      tradeCount: baseTrades.length,
      wins: wins.length,
      losses: baseTrades.length - wins.length,
      wr,
      avgR,
      topWinningConf,
      hourFit,
      recommended: recommendedStatus,
      missingRecommended,
      mostRecent,
    };
  }, [setup, atmStrategy, direction, entryTime, trade?.confluences,
      allTrades, allConfluences, setupDetails, getTradeNetPnL, getAccountById]);

  if (!setup) return null;

  // Empty state when setup is picked but no historical data exists yet
  if (!insights) {
    return (
      <div className={`mb-3 rounded-lg border p-3 ${
        darkMode
          ? 'bg-purple-500/5 border-purple-500/20'
          : 'bg-purple-50/50 border-purple-200'
      }`}>
        <div className="flex items-center gap-2">
          <Brain size={14} className={darkMode ? 'text-purple-300' : 'text-purple-700'} />
          <span className={`text-xs font-semibold uppercase tracking-wider ${darkMode ? 'text-purple-200' : 'text-purple-800'}`}>
            Pre-Trade Coach
          </span>
        </div>
        <p className={`text-xs mt-1.5 ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          No historical trades yet for "{setup}" — Coach will start surfacing context once you have a few completed trades on this setup.
        </p>
      </div>
    );
  }

  // Surface a strong-warn color when WR is below 40% — the "you sure?" tier
  const isWeak = insights.wr < 0.4 && insights.tradeCount >= 4;
  const isStrong = insights.wr >= 0.6 && insights.tradeCount >= 4;
  const accentClass = isWeak
    ? (darkMode ? 'bg-amber-500/5 border-amber-500/30' : 'bg-amber-50/60 border-amber-300')
    : isStrong
      ? (darkMode ? 'bg-emerald-500/5 border-emerald-500/30' : 'bg-emerald-50/60 border-emerald-300')
      : (darkMode ? 'bg-purple-500/5 border-purple-500/20' : 'bg-purple-50/50 border-purple-200');
  const iconColor = isWeak
    ? (darkMode ? 'text-amber-300' : 'text-amber-700')
    : isStrong
      ? (darkMode ? 'text-emerald-300' : 'text-emerald-700')
      : (darkMode ? 'text-purple-300' : 'text-purple-700');

  return (
    <div className={`mb-3 rounded-lg border p-3 ${accentClass}`}>
      {/* Header */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Brain size={14} className={iconColor} />
          <span className={`text-xs font-semibold uppercase tracking-wider ${iconColor}`}>
            Pre-Trade Coach
          </span>
          <span className={`text-[10px] ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            · {insights.matchLabel}
          </span>
        </div>
        {insights.mostRecent && typeof onJumpToTrade === 'function' && insights.mostRecent.id !== trade?.id && (
          <button
            type="button"
            onClick={() => onJumpToTrade(insights.mostRecent.id, insights.mostRecent.date)}
            className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border ${
              darkMode ? 'border-white/10 text-gray-300 hover:bg-white/5' : 'border-gray-300 text-gray-600 hover:bg-gray-50'
            }`}
            title={`Jump to most recent ${insights.matchLabel} (${insights.mostRecent.date})`}
          >
            <ExternalLink size={9} />
            See most recent
          </button>
        )}
      </div>

      {/* Headline stats */}
      <div className="grid grid-cols-3 gap-2 mb-2">
        <div className={`rounded-md p-2 ${darkMode ? 'bg-black/30' : 'bg-white/60'}`}>
          <div className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>WR</div>
          <div className={`text-base font-bold tabular-nums ${
            isWeak ? (darkMode ? 'text-amber-300' : 'text-amber-700')
            : isStrong ? (darkMode ? 'text-emerald-300' : 'text-emerald-700')
            : (darkMode ? 'text-white' : 'text-gray-900')
          }`}>
            {fmtPct(insights.wr)}
          </div>
        </div>
        <div className={`rounded-md p-2 ${darkMode ? 'bg-black/30' : 'bg-white/60'}`}>
          <div className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Trades</div>
          <div className={`text-base font-bold tabular-nums ${darkMode ? 'text-white' : 'text-gray-900'}`}>
            {insights.tradeCount}
            <span className={`text-[10px] font-normal ml-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
              {insights.wins}W / {insights.losses}L
            </span>
          </div>
        </div>
        <div className={`rounded-md p-2 ${darkMode ? 'bg-black/30' : 'bg-white/60'}`}>
          <div className={`text-[10px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>Avg R</div>
          <div className={`text-base font-bold tabular-nums ${
            insights.avgR != null && insights.avgR > 0
              ? (darkMode ? 'text-emerald-300' : 'text-emerald-700')
              : insights.avgR != null && insights.avgR < 0
                ? (darkMode ? 'text-red-300' : 'text-red-700')
                : (darkMode ? 'text-white' : 'text-gray-900')
          }`}>
            {fmtR(insights.avgR)}
          </div>
        </div>
      </div>

      {/* Time-of-day fit */}
      {insights.hourFit && Math.abs(insights.hourFit.delta) >= 0.10 && (
        <div className={`flex items-start gap-1.5 text-[11px] mb-1.5 ${darkMode ? 'text-gray-300' : 'text-gray-700'}`}>
          <Clock size={11} className={`mt-0.5 shrink-0 ${insights.hourFit.delta > 0 ? (darkMode ? 'text-emerald-400' : 'text-emerald-600') : (darkMode ? 'text-amber-400' : 'text-amber-600')}`} />
          <span>
            {insights.hourFit.delta > 0
              ? `${insights.hourFit.window} is a strong window for this setup — ${fmtPct(insights.hourFit.hourWr)} WR (${insights.hourFit.hourCount} trades) vs ${fmtPct(insights.hourFit.baselineWr)} baseline.`
              : `${insights.hourFit.window} is a weak window — ${fmtPct(insights.hourFit.hourWr)} WR (${insights.hourFit.hourCount} trades) vs ${fmtPct(insights.hourFit.baselineWr)} baseline.`}
          </span>
        </div>
      )}

      {/* Top winning confluences */}
      {insights.topWinningConf.length > 0 && (
        <div className="mb-1.5">
          <div className={`flex items-center gap-1.5 text-[10px] uppercase tracking-wider mb-1 ${darkMode ? 'text-gray-500' : 'text-gray-500'}`}>
            <TrendingUp size={10} />
            Winning recipe (in ≥40% of winners)
          </div>
          <div className="flex flex-wrap gap-1.5">
            {insights.topWinningConf.map(c => (
              <span
                key={c.id}
                className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full border ${
                  c.currentlyTagged
                    ? (darkMode ? 'bg-emerald-500/15 border-emerald-500/30 text-emerald-300' : 'bg-emerald-100 border-emerald-300 text-emerald-700')
                    : (darkMode ? 'bg-white/5 border-white/15 text-gray-300' : 'bg-white border-gray-300 text-gray-700')
                }`}
                title={c.currentlyTagged ? 'Already flagged on this trade' : 'Not yet flagged — consider checking'}
              >
                {c.currentlyTagged && <CheckCircle2 size={9} />}
                {c.label}
                <span className="opacity-70">{fmtPct(c.winnerShare)}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Missing recommended confluences */}
      {insights.missingRecommended.length > 0 && (
        <div className={`flex items-start gap-1.5 text-[11px] ${darkMode ? 'text-amber-300' : 'text-amber-700'}`}>
          <AlertTriangle size={11} className="mt-0.5 shrink-0" />
          <span>
            Missing recommended: {insights.missingRecommended.map(r => r.label).join(', ')} — your pinned filter for this setup says these should be present.
          </span>
        </div>
      )}

      {/* Strong-context praise message — only when truly strong */}
      {isStrong && insights.missingRecommended.length === 0 && insights.topWinningConf.every(c => c.currentlyTagged) && (
        <div className={`flex items-start gap-1.5 text-[11px] ${darkMode ? 'text-emerald-300' : 'text-emerald-700'}`}>
          <Target size={11} className="mt-0.5 shrink-0" />
          <span>Setup, ATM, and confluence pattern all match your historical winners. Trade with conviction — but still respect the stop.</span>
        </div>
      )}
    </div>
  );
};

export default PreTradeCoachPanel;
